import type { LanesColumn } from "@repo/lanes/types";

import type { RiftData } from "~/stores/dataset-rifts/rift-data";

/** Escape a cell value for CSV per RFC 4180. */
const escapeCell = (value: unknown): string => {
  if (value == null) return "";
  const str = typeof value === "object" ? JSON.stringify(value) : String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Escape a temporal cell as an ISO 8601 UTC string. Arrow encodes every
 * temporal column as `TimestampMillisecond`, so cells arrive as epoch
 * milliseconds and would otherwise reach the file as bare numbers. Values that
 * cannot be read as an instant fall back to the default escaping rather than
 * being dropped.
 */
const escapeDateCell = (value: unknown): string => {
  if (value == null) return "";

  const ms =
    value instanceof Date
      ? value.getTime()
      : typeof value === "number"
        ? value
        : typeof value === "bigint"
          ? Number(value)
          : Date.parse(String(value));

  const date = new Date(ms);
  // Catches both unparseable input and instants outside the Date range, either
  // of which would make `toISOString()` throw.
  if (Number.isNaN(date.getTime())) return escapeCell(value);

  // ISO 8601 contains no comma, quote or newline, so it needs no quoting.
  return date.toISOString();
};

/** Picks the cell serialiser for a column from its resolved data type. */
const serializerFor = (column: LanesColumn | undefined) =>
  column?.type === "DATE" ? escapeDateCell : escapeCell;

/** Rows materialised per batch when serialising a `RiftData` to CSV. */
const CSV_BATCH_ROWS = 10_000;

/**
 * Serialises rift data to CSV. Rows are materialised from the Arrow-backed
 * `RiftData` in batches so a full-dataset export never holds every row object
 * in memory at once.
 */
export const convertToCSV = (
  columns: Record<string, LanesColumn>,
  data: RiftData,
): string => {
  // Resolve each column's serialiser once rather than per cell — a full export
  // calls it `numRows` times per column otherwise.
  const fields = Object.keys(columns).map((name) => ({
    name,
    serialize: serializerFor(columns[name]),
  }));
  const lines: string[] = [
    fields.map((field) => escapeCell(field.name)).join(","),
  ];

  for (let start = 0; start < data.numRows; start += CSV_BATCH_ROWS) {
    const end = Math.min(start + CSV_BATCH_ROWS, data.numRows);
    const rows = data.toRows(start, end);
    for (const row of rows) {
      lines.push(
        fields.map((field) => field.serialize(row[field.name])).join(","),
      );
    }
  }

  return lines.join("\n");
};

/** Trigger a browser download of already-serialised CSV text. */
export const downloadCsvString = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/** Trigger a browser download of rift data as a CSV file. */
export const downloadCSV = (
  filename: string,
  columns: Record<string, LanesColumn>,
  data: RiftData,
) => {
  downloadCsvString(filename, convertToCSV(columns, data));
};
